// Copyright (C) 2012-present, The Authors. This program is free software: you can redistribute it and/or  modify it under the terms of the GNU Affero General Public License, version 3, as published by the Free Software Foundation. This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU Affero General Public License for more details. You should have received a copy of the GNU Affero General Public License along with this program.  If not, see <http://www.gnu.org/licenses/>.

import PolisNet from '../../../util/net'
import { useState, useEffect } from 'react'
import Url from '../../../util/url'
import { useSelector, useDispatch } from 'react-redux'
import { Heading, Box, Button, Text } from 'theme-ui'
import { useAuth } from 'react-oidc-context'
import DanskFooter from '../../shared/DanskFooter'
import { populateZidMetadataStore } from '../../../actions'
import ComponentHelpers from '../../../util/component-helpers'
import NoPermission from '../no-permission'
import { useParams } from 'react-router'

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  // Convert to number if it's a string, and handle milliseconds
  const numericTimestamp = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
  const date = new Date(numericTimestamp);
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const ReportsList = () => {
  const dispatch = useDispatch()
  const params = useParams()
  const { isAuthenticated } = useAuth()
  const zid_metadata = useSelector((state) => state.zid_metadata)

  const [state, setState] = useState({
    loading: true,
    reports: [],
    dataLoaded: false
  })
  
  const [newlyAddedReportId, setNewlyAddedReportId] = useState(null)

  const getData = () => {
    const reportsPromise = PolisNet.polisGet('/api/v3/reports', {
      conversation_id: params.conversation_id
    })
    reportsPromise.then((reports) => {
      // Sort reports by created timestamp, newest first
      const sortedReports = reports.sort((a, b) => {
        const timeA = typeof a.created === 'string' ? parseInt(a.created, 10) : a.created;
        const timeB = typeof b.created === 'string' ? parseInt(b.created, 10) : b.created;
        return timeB - timeA; // Newest first
      });
      
      setState({
        loading: false,
        reports: sortedReports,
        dataLoaded: true
      })
    })
  }

  const loadInitialData = () => {
    if (isAuthenticated) {
      dispatch(populateZidMetadataStore(params.conversation_id))
    }
  }

  useEffect(() => {
    loadInitialData()

    // Check if user is already a moderator on mount
    if (zid_metadata?.zid_metadata?.is_mod && !state.dataLoaded) {
      getData()
    }
  }, [])

  useEffect(() => {
    const currentIsMod = zid_metadata?.zid_metadata?.is_mod

    // Load data if user is now a moderator and data hasn't been loaded
    if (currentIsMod && !state.dataLoaded) {
      getData()
    }

    if (isAuthenticated && !zid_metadata?.zid_metadata) {
      loadInitialData()
    }
  }, [zid_metadata, isAuthenticated])

  const createReportClicked = () => {
    PolisNet.polisPost('/api/v3/reports', {
      conversation_id: params.conversation_id
    }).then(() => {
      // Get the previous report count to identify the new report
      const previousReportIds = state.reports.map(r => r.report_id);
      
      // Fetch updated data
      const reportsPromise = PolisNet.polisGet('/api/v3/reports', {
        conversation_id: params.conversation_id
      })
      reportsPromise.then((reports) => {
        // Sort reports by created timestamp, newest first
        const sortedReports = reports.sort((a, b) => {
          const timeA = typeof a.created === 'string' ? parseInt(a.created, 10) : a.created;
          const timeB = typeof b.created === 'string' ? parseInt(b.created, 10) : b.created;
          return timeB - timeA; // Newest first
        });
        
        // Find the newly added report (should be first after sorting)
        const newReport = sortedReports.find(r => !previousReportIds.includes(r.report_id));
        if (newReport) {
          setNewlyAddedReportId(newReport.report_id);
          // Clear the animation flag after animation completes
          setTimeout(() => setNewlyAddedReportId(null), 600);
        }
        
        setState({
          loading: false,
          reports: sortedReports,
          dataLoaded: true
        })
      })
    })
  }

  if (
    ComponentHelpers.shouldShowPermissionsError({
      zid_metadata: zid_metadata.zid_metadata,
      loading: zid_metadata.loading
    })
  ) {
    return <NoPermission />
  }

  if (state.loading) {
    return <div>Henter rapporter</div>
  }

  return (
    <Box>
      <Heading
        as="h3"
        sx={{
          fontSize: [3, null, 4],
          lineHeight: 'body',
          mb: [3, null, 4]
        }}>
        Rapporter
      </Heading>

        <Box sx={{ mb: [3, null, 4] }}>
          <Text sx={{ display: 'block', mb: [2] }}>
            Generér en aktuel rapport over denne samtale, inkl. alle indlæg fra deltagerne. Du kan generere så mange rapporter, du vil, og de vil alle være tilgængelige her.        
          </Text>
        </Box>

      <Box sx={{ mb: [3, null, 4] }}>
        <Button onClick={createReportClicked}>Generér rapport</Button>
      </Box>

      <Box sx={{ 
        '& > *': {
          transition: 'transform 0.3s ease-out, margin 0.3s ease-out'
        }
      }}>
        {state.reports.map((report, index) => {
        const isNewlyAdded = report.report_id === newlyAddedReportId;
        
        return (
          <Box 
            as="a"
            href={Url.urlPrefix + 'report/' + report.report_id}
            target="_blank"
            rel="noreferrer"
            sx={{ 
              mb: [2],
              p: [2, 3],
              bg: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              transition: 'all 0.2s ease',
              textDecoration: 'none',
              color: 'inherit',
              display: 'block',
              cursor: 'pointer',
              // Animation for newly added reports
              ...(isNewlyAdded ? {
                animation: 'slideInFromTop 0.5s ease-out',
                '@keyframes slideInFromTop': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(-20px)'
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)'
                  }
                }
              } : {
                // Smooth transition for existing reports being pushed down
                transition: 'all 0.3s ease-out'
              }),
              '&:hover': {
                bg: '#f1f3f4',
                borderColor: '#dee2e6',
                textDecoration: 'none',
                color: 'inherit'
              }
            }} 
            key={report.report_id} 
            data-testid="report-list-item"
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 3,
              fontFamily: 'monospace',
              fontSize: [1, 2]
            }}>
              <Box sx={{ 
                color: '#495057', 
                fontSize: [0, 1], 
                minWidth: '130px',
                fontWeight: '500',
                bg: '#e9ecef',
                px: 2,
                py: 1,
                borderRadius: '4px',
                display: 'inline-block'
              }}>
                {formatTimestamp(report.created)}
              </Box>
              <Box 
                sx={{
                  color: '#0066cc',
                  fontSize: '14px',
                  fontWeight: '400',
                  wordBreak: 'break-all'
                }}
              >
                {Url.urlPrefix}report/{report.report_id}
              </Box>
            </Box>
          </Box>
        )
      })}
      </Box>
      <DanskFooter />
    </Box>
  )
}

export default ReportsList
